import * as hex from './hex';

declare interface metaWindow extends Window {
    fbq?: (...args: unknown[]) => void;
    metaPixel?: string;
}
declare const window: metaWindow;

// InitializeMeta inits the Meta (Facebook) pixel, optionally with advanced
// matching. Meta hashes the raw email/phone/name client-side, so pass them
// unhashed — the pixel SHA-256s them before they leave the browser.
//
// userId is the logged-in customer's bid2 id (sess.user.id). It's hex-encoded
// into Meta's external_id so it matches the server-side Conversions API value
// (go/conversions externalID), letting Meta link the browser and server events
// to one identity. external_id is an opaque id, so the pixel sends it as-is.
export function InitializeMeta(pixelId: string, opts?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    userId?: string;
}): void {
    if (!window.fbq) {
        return;
    }

    const advancedMatching: Record<string, string> = {};

    if (opts?.email) {
        advancedMatching.em = opts.email;
    }

    if (opts?.phone) {
        advancedMatching.ph = opts.phone;
    }

    if (opts?.firstName) {
        advancedMatching.fn = opts.firstName;
    }

    if (opts?.lastName) {
        advancedMatching.ln = opts.lastName;
    }

    if (opts?.userId) {
        const externalId = hex.Base64URLToHex(opts.userId);
        if (externalId) {
            advancedMatching.external_id = externalId;
        }
    }

    if (Object.keys(advancedMatching).length) {
        window.fbq('init', pixelId, advancedMatching);
    } else {
        window.fbq('init', pixelId);
    }
}

export function TrackPageView(eventId?: string): void {
    if (!window.fbq) {
        return;
    }

    if (eventId) {
        window.fbq('track', 'PageView', {}, { eventID: eventId });
    } else {
        window.fbq('track', 'PageView');
    }
}

// TrackUnqualifiedLead reports a raw, unreviewed submission as Meta's custom
// `UnqualifiedLead` event instead of the standard `Lead` (#6790/#7362) —
// custom events need no Events Manager setup and never optimize ad delivery
// unless explicitly selected. The qualified, optimizing `Lead` fires
// server-side via the Conversions API when the artwork task is created
// (#6300); different event names mean the two coexist rather than dedup, by
// design. eventId still dedups duplicate browser fires of this event.
export function TrackUnqualifiedLead(opts: {
    value?: number;
    currency?: string;
    eventId: string;
}): void {
    if (!window.fbq) {
        return;
    }

    const data: Record<string, unknown> = {};

    if (opts.value !== undefined) {
        data.value = opts.value;
        data.currency = opts.currency || 'USD';
    }

    window.fbq('trackCustom', 'UnqualifiedLead', data, { eventID: opts.eventId });
}
